import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Target, MapPin, Activity, FileText, Building2, TrendingUp, FlaskConical, ChevronDown, ChevronRight, Copy, Check, Download, Info } from 'lucide-react';

const API = '/api';

/**
 * Generalize a verbose contact designation down to a coarse seniority +
 * function label so a biotech reading the public share link can't
 * cross-reference (Pharma + exact title + LinkedIn photo) to identify
 * the person. Per user request (2026-02): "since I usually put up the
 * LinkedIn video profile on the public shareable link, they can easily
 * look out for that particular person if they want because there's the
 * name of the company and the designation."
 *
 * Examples:
 *   "Regional Vice President - Business Development & Alliance Management"
 *     → "VP, BD & Licensing"
 *   "Senior Director - In-Licensing & Strategy"  → "Sr. Director, BD & Licensing"
 *   "Head of Search & Evaluation"                → "Head, BD & Licensing"
 *   "Associate Director - Strategic Marketing"   → "Director-level, Commercial"
 *   "VP, Medical Affairs"                        → "VP, Medical Affairs"
 *
 * Returns null when the input is empty (caller can decide what to render).
 */
function coarseDesignation(d) {
  if (!d) return null;
  const s = String(d).toLowerCase();

  // Seniority — order matters (most specific first)
  let level = 'Team';
  if (/\b(ceo|coo|cso|cto|cfo|cmo|cbo)\b|chief\s/.test(s)) level = 'C-level';
  else if (/president(?!.*vice)|\bpresident\b/.test(s) && !/vice\s+president/.test(s)) level = 'President';
  else if (/\bevp\b|executive vice president/.test(s)) level = 'EVP';
  else if (/\bsvp\b|senior vice president/.test(s)) level = 'SVP';
  else if (/\bvp\b|vice president/.test(s)) level = 'VP';
  else if (/general manager|country manager|managing director/.test(s)) level = 'GM/MD';
  else if (/senior director|sr\.?\s*director/.test(s)) level = 'Sr. Director';
  else if (/associate director/.test(s)) level = 'Director-level';
  else if (/\bdirector\b/.test(s)) level = 'Director';
  else if (/\bhead\b/.test(s)) level = 'Head';
  else if (/senior manager|sr\.?\s*manager/.test(s)) level = 'Sr. Manager';
  else if (/\bmanager\b/.test(s)) level = 'Manager';
  else if (/\blead\b/.test(s)) level = 'Lead';
  else if (/principal|\bstaff\b/.test(s)) level = 'Principal';
  else if (/\bassociate\b/.test(s)) level = 'Associate';

  // Coarse function — covers ~95% of pharma BD-adjacent contacts
  let func = null;
  if (/business development|\bbd\b|alliance|licensing|in-licens|out-licens|search.*evaluat|\bs&e\b|partnering|partnerships/.test(s)) func = 'BD & Licensing';
  else if (/portfolio|strategy|strategic(?!\s*marketing)/.test(s)) func = 'Strategy';
  else if (/medical/.test(s)) func = 'Medical Affairs';
  else if (/clinical|trial|regulatory|\bra\b/.test(s)) func = 'Clinical / Regulatory';
  else if (/research|discovery|preclinical|r&d|translational/.test(s)) func = 'R&D';
  else if (/commercial|marketing|sales|brand|launch/.test(s)) func = 'Commercial';
  else if (/operation|supply|manufactur/.test(s)) func = 'Operations';
  else if (/legal|ip\b|intellectual property/.test(s)) func = 'Legal / IP';
  else if (/finance|investor|treasur/.test(s)) func = 'Finance';

  return func ? `${level}, ${func}` : level;
}


/** Small inline "copy to clipboard" button. DISABLED on the public share
 * page (user 2026-02: "none of the text should be copyable on shareable
 * link"). Kept as a no-op stub instead of removed entirely so existing
 * call sites stay valid without a wider refactor. Renders nothing. */
// eslint-disable-next-line no-unused-vars
function CopyButton(_props) {
  return null;
}

/** Convert full company name to initials — "Oxford Science Enterprises" → "OSE" */
function toInitials(name) {
  if (!name) return '—';
  return name
    .split(/[\s\-/]+/)
    .filter(w => w.length > 0)
    .map(w => w[0].toUpperCase())
    .join('');
}

/** Color-code development stages so they're scannable at a glance. */
function stageColor(stage) {
  const s = (stage || '').toLowerCase();
  if (!s) return 'bg-slate-50 text-slate-500 border-slate-200';
  if (s.includes('approved') || s.includes('marketed') || s.includes('commercial')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (s.includes('filed') || s.includes('nda') || s.includes('bla')) return 'bg-teal-50 text-teal-700 border-teal-200';
  if (s.includes('phase 3') || s.includes('phase iii')) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (s.includes('phase 2') || s.includes('phase ii')) return 'bg-violet-50 text-violet-700 border-violet-200';
  if (s.includes('phase 1') || s.includes('phase i')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  if (s.includes('preclinical')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (s.includes('platform')) return 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200';
  if (s.includes('clinical')) return 'bg-violet-50 text-violet-700 border-violet-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
}

const STATUS_COLORS = {
  'Introduction Made': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Email Acknowledged': 'bg-lime-50 text-lime-700 border-lime-200',
  'Asset Under Evaluation': 'bg-amber-50 text-amber-700 border-amber-200',
  'Meeting Requested': 'bg-violet-50 text-violet-600 border-violet-200',
  'Meeting Scheduled': 'bg-violet-50 text-violet-700 border-violet-200',
  'CDA in Progress': 'bg-blue-50 text-blue-600 border-blue-200',
  'CDA Signed': 'bg-blue-50 text-blue-700 border-blue-200',
  'Awaiting Trial Data': 'bg-orange-50 text-orange-700 border-orange-200',
  'Due Diligence': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'In Negotiation': 'bg-purple-50 text-purple-700 border-purple-200',
  'Deal Discussion': 'bg-purple-50 text-purple-700 border-purple-200',
  'Deal Closed': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Declined': 'bg-rose-50 text-rose-600 border-rose-200',
  'Not Interested': 'bg-rose-50 text-rose-600 border-rose-200',
  'Ghosted': 'bg-stone-50 text-stone-500 border-stone-200',
  'Deal Lost': 'bg-red-50 text-red-600 border-red-200',
  'On Hold': 'bg-slate-50 text-slate-600 border-slate-200',
};

// Plain-language definitions surfaced as hover-tooltips on the public status
// page so the biotech's clients understand what each pharma response means.
// The most important one is "Asset Under Evaluation" — this is the milestone
// where ForesightRx has shared the non-confidential deck and the pharma's
// internal team has started reviewing it.
const STATUS_DEFINITIONS = {
  'Introduction Made': 'Initial outreach sent to introduce the asset and the company behind it.',
  'Email Acknowledged': 'Pharma contact has acknowledged receipt of the introduction — no substantive response yet, but the message landed.',
  'Asset Under Evaluation': "Non-confidential deck has been shared at the pharma's request; the asset is currently being evaluated by their internal team.",
  'Meeting Requested': 'One of the parties has proposed a call or meeting; a time has not been fixed yet.',
  'Meeting Scheduled': 'A formal call or in-person meeting has been scheduled to discuss the opportunity.',
  'CDA in Progress': 'A confidentiality agreement (CDA/NDA) is being drafted, reviewed or negotiated between the parties.',
  'CDA Signed': 'Mutual confidentiality agreement signed; confidential data exchange has begun.',
  'Awaiting Trial Data': 'Pharma is interested but waiting for upcoming clinical trial readouts before progressing.',
  'Due Diligence': 'Pharma is conducting formal due diligence on the asset.',
  'In Negotiation': 'Active commercial discussions on terms and deal structure.',
  'Deal Discussion': 'Active commercial discussions on terms and deal structure.',
  'Deal Closed': 'Definitive agreement signed.',
  'Declined': 'The pharma is not pursuing the opportunity at this time.',
  'Not Interested': 'The pharma is not pursuing the opportunity at this time.',
  'Ghosted': 'No response received despite multiple follow-ups.',
  'Deal Lost': 'Negotiations concluded without a deal.',
  'On Hold': 'Discussions paused — to be resumed at a later date.',
};

/** An asset is "active" if it has at least a target list (AI mapping done) or emails sent. */
function isActive(asset) {
  // An asset is "active" if it has AI-mapped targets, sent emails, ANY
  // logged response_rows, OR a bulk email campaign initiated/completed
  // (emailing_status) while not hidden. The last clause catches assets like
  // GR1901 (Email: In Progress, visible) that had no delivered sends or
  // manual rows yet (user report Jul 2026).
  return (asset.targets && asset.targets.length > 0)
    || (asset.emails_sent || 0) > 0
    || (asset.response_rows && asset.response_rows.length > 0)
    || (!asset.is_hidden && ['In Progress', 'Completed'].includes(asset.emailing_status));
}

export default function PublicStatusPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/status/${token}`)
      .then(res => setData(res.data))
      .catch(() => setError('This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-5 h-5 border-2 border-slate-800 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Card className="max-w-md w-full mx-4 border border-slate-200">
          <CardContent className="p-8 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="text-base font-semibold text-slate-900 mb-1">Link Not Found</p>
            <p className="text-sm text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const assets = data?.assets || [];
  const activeAssets = assets.filter(isActive).sort((a, b) => (b.emails_sent || 0) - (a.emails_sent || 0));
  const inactiveAssets = assets.filter(a => !isActive(a));

  // Investor response rollup (aggregate — investors apply to the company as a whole).
  const investorResponses = (data?.responses || []).filter(r => (r.contact_type || '').toLowerCase() === 'investor');

  // Pharma responses NOT already attached to a specific asset card. These
  // would otherwise be invisible to the public tracker — they live in
  // `data.responses` but are never rendered (per-asset response_rows requires
  // a matching asset_id). Collect them here and surface in a top-level
  // "Pharma Outreach" section. Dedupe by company name to avoid duplicates
  // across the per-asset and company-level sections.
  const perAssetPharmaCompanies = new Set();
  (data?.assets || []).forEach(a => {
    (a.response_rows || []).forEach(r => {
      const cn = (r.company_name || '').trim().toLowerCase();
      if (cn) perAssetPharmaCompanies.add(cn);
    });
  });
  const companyLevelPharmaResponses = (data?.responses || [])
    .filter(r => (r.contact_type || '').toLowerCase() !== 'investor')
    .filter(r => {
      const cn = (r.company_name || '').trim().toLowerCase();
      return cn && !perAssetPharmaCompanies.has(cn);
    });

  // Assemble a plain-text report of everything on the page. Used by the
  // "Copy full report" button in the hero — lets users paste a clean update
  // into an email without drag-selecting across cards.
  const fullReport = (() => {
    const lines = [];
    lines.push(`${data.company_name} — Deal Advisory Status`);
    if (data.therapeutic_area) lines.push(data.therapeutic_area);
    lines.push('');
    activeAssets.forEach((a, i) => {
      const stageBit = a.stage ? ` (${a.stage})` : '';
      lines.push(`${i + 1}. ${a.name || '—'}${stageBit}`);
      if (a.indication) lines.push(`   Indication: ${a.indication}`);
      if (a.brief) lines.push(`   ${a.brief}`);
      const rows = a.response_rows || [];
      if (rows.length > 0) {
        lines.push('   Responses:');
        rows.forEach((r) => {
          const who = r.company_name || '—';
          const status = r.status ? ` — ${r.status}` : '';
          const notes = r.status_notes ? ` (${r.status_notes})` : '';
          lines.push(`     • ${who}${status}${notes}`);
        });
      }
      lines.push('');
    });
    if (investorResponses.length > 0) {
      lines.push('Investor Responses:');
      investorResponses.forEach((r) => {
        const status = r.status ? ` — ${r.status}` : '';
        const notes = r.status_notes ? ` (${r.status_notes})` : '';
        lines.push(`  • [Investor]${status}${notes}`);
      });
      lines.push('');
    }
    if (companyLevelPharmaResponses.length > 0) {
      lines.push('Pharma Outreach (Company-Level):');
      companyLevelPharmaResponses.forEach((r) => {
        const who = r.company_name || '—';
        const status = r.status ? ` — ${r.status}` : '';
        const notes = r.status_notes ? ` (${r.status_notes})` : '';
        lines.push(`  • ${who}${status}${notes}`);
      });
      lines.push('');
    }
    lines.push('— Powered by ForesightRx, a division of Marksman Healthcare');
    return lines.join('\n');
  })();

  return (
    <div
      className="min-h-screen bg-slate-50/30 select-none"
      data-testid="public-status-page"
      onCopy={(e) => { e.preventDefault(); }}
      onCut={(e) => { e.preventDefault(); }}
      onContextMenu={(e) => { e.preventDefault(); }}
      onDragStart={(e) => { e.preventDefault(); }}
      style={{
        // Disable text selection on every platform — keeps the contact
        // generalisation + share-link revocation defenses meaningful.
        // Users can still take screenshots, but they can't drag-select
        // and paste pharma names / contact roles in bulk.
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        userSelect: 'none',
      }}
    >
      {/* Top bar */}
      <div className="border-b border-slate-100 bg-white">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400" style={{ letterSpacing: '0.15em' }}>
            ForesightRx
          </span>
          <span className="text-[10px] text-slate-400">Deal Advisory Status</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8">
        {/* Hero section — ForesightRx intro + Company */}
        <div className="mb-6 rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid="hero-section">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 py-6">
            <div className="flex items-start gap-5">
              <div className="shrink-0 mt-0.5">
                <img
                  src="https://customer-assets.emergentagent.com/job_licensing-hub-3/artifacts/0s2i33uj_MM_Forsight%20Logo%20Png.png"
                  alt="ForesightRx"
                  className="h-9 w-auto brightness-0 invert"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/90 text-sm leading-relaxed">
                  <span className="font-semibold text-white">ForesightRx</span> is the deal advisory arm of Marksman Healthcare, specializing in cross-border business development for life sciences. We connect biotech innovators with the right pharma partners and investors — managing strategic matchmaking, targeted outreach, and fundraising support to help early and mid-stage companies scale globally.
                </p>
              </div>
            </div>
          </div>
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold text-slate-900" data-testid="public-company-name" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {data.company_name}
                </h1>
                {data.therapeutic_area && (
                  <p className="text-xs text-slate-500 mt-1 uppercase tracking-wide">{data.therapeutic_area}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {data.has_ncd_deck && (
                  <a
                    href={`${API}/public/status/${token}/ncd-deck`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                    data-testid="download-ncd-deck"
                    title={data.ncd_filename ? `Download ${data.ncd_filename}` : 'Download Non-Confidential Deck'}
                  >
                    <Download className="w-3 h-3" />
                    <span>Non-Confidential Deck</span>
                  </a>
                )}
                <CopyButton text={fullReport} label="Copy full report" testid="copy-full-report" />
              </div>
            </div>
          </div>

          {/* Slim top-line stats — portfolio overview only */}
          <div className="px-6 py-4 grid grid-cols-2 gap-4" data-testid="coverage-summary">
            <Stat label="Portfolio Assets" value={assets.length} icon={FlaskConical} tone="fuchsia" />
            <Stat label="Under Active Outreach" value={activeAssets.length} icon={Activity} tone="emerald" />
          </div>
        </div>

        {/* ASSET-CENTRIC: one rich card per active asset. Each has its OWN brief,
            targets, stats, and responses — no company-wide brief needed. */}
        {activeAssets.length > 0 && (
          <div className="mb-6 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Active Outreach</h2>
              <span className="text-[10px] text-slate-400">({activeAssets.length} of {assets.length})</span>
            </div>
            {activeAssets.map((a, i) => (
              <AssetCard key={a.id || i} asset={a} index={i + 1} />
            ))}
          </div>
        )}

        {/* Inactive assets — collapsed compact list */}
        {inactiveAssets.length > 0 && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowInactive(!showInactive)}
              className="w-full flex items-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
              data-testid="toggle-inactive-assets"
            >
              {showInactive ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Other Portfolio Assets</span>
              <span className="text-[10px] text-slate-400">({inactiveAssets.length} · outreach not initiated)</span>
            </button>
            {showInactive && (
              <Card className="mt-2 border border-slate-200 overflow-hidden bg-white">
                <div className="divide-y divide-slate-100">
                  {inactiveAssets.map((a, i) => (
                    <div key={a.id || i} className="px-5 py-3 flex items-start gap-3 opacity-75" data-testid={`inactive-asset-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-700">{a.name || '—'}</span>
                          {a.stage && <Badge className={`text-[10px] border ${stageColor(a.stage)}`}>{a.stage}</Badge>}
                          {a.therapeutic_area && <span className="text-[11px] text-slate-400">· {a.therapeutic_area}</span>}
                        </div>
                        {a.indication && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1" title={a.indication}>{a.indication}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Investor Response Tracker — aggregate (investors apply to the company, not per-asset) */}
        {investorResponses.length > 0 && (
          <div className="mb-8" data-testid="investor-response-section">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <h2 className="text-sm font-semibold text-slate-900">Investor Response Tracker</h2>
              <span className="text-[10px] text-slate-400 ml-1">({investorResponses.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {investorResponses.map((entry, i) => (
                <ResponseRow key={i} entry={entry} testid={`investor-response-${i}`} anonymize />
              ))}
            </div>
          </div>
        )}

        {/* Pharma Outreach (Company-Level) — pharma responses not tied to any
            specific asset. Captures hand-logged introductions and account-level
            engagements that would otherwise be hidden because the per-asset
            cards only show responses with a matching asset_id. */}
        {companyLevelPharmaResponses.length > 0 && (
          <div className="mb-8" data-testid="pharma-company-level-section">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-sky-600" />
              <h2 className="text-sm font-semibold text-slate-900">Pharma Outreach (Company-Level)</h2>
              <span className="text-[10px] text-slate-400 ml-1">({companyLevelPharmaResponses.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {companyLevelPharmaResponses.map((entry, i) => (
                <ResponseRow key={i} entry={entry} testid={`pharma-company-response-${i}`} compact />
              ))}
            </div>
          </div>
        )}

        {/* Closing statement — sets expectations for what happens after pharma evaluation */}
        <div className="mb-6 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-5 py-4" data-testid="closing-statement">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mt-0.5">
              <Building2 className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">What Happens Next</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                Once a pharma partner completes their evaluation and signals genuine interest, ForesightRx will coordinate the next step — whether that's a virtual call, in-person meeting, or further data exchange — and loop you in directly.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-6 border-t border-slate-100 text-center">
          <p className="text-[11px] text-slate-400">
            Powered by <span className="font-semibold text-slate-500">ForesightRx</span> — A division of Marksman Healthcare
          </p>
        </div>
      </div>
    </div>
  );
}

/** A single active-asset card with its OWN targets + responses + stats. */
function AssetCard({ asset, index }) {
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [expandedTargets, setExpandedTargets] = useState(() => new Set());

  const positive = asset.positive_responses || 0;
  // "Declined" tile shows unique COMPANIES that have declined (at least one
  // declined contact), matching the internal Biotech profile chip semantics.
  // Server provides `declined_companies` (preferred); falls back to
  // `declined_contacts` then `negative_responses` for backward compat with
  // older payloads.
  const negative = (typeof asset.declined_companies === 'number')
    ? asset.declined_companies
    : (typeof asset.declined_contacts === 'number'
        ? asset.declined_contacts
        : (asset.negative_responses || 0));
  const awaitingTrial = asset.awaiting_trial_responses || 0;
  const pharmas = asset.pharmas_engaged || 0;
  const targets = asset.targets || [];
  const responses = asset.response_rows || [];
  const selfManaged = Array.isArray(asset.client_excluded_pharmas) ? asset.client_excluded_pharmas : [];

  return (
    <Card className="border border-slate-200 overflow-hidden bg-white border-l-4 border-l-emerald-500" data-testid={`asset-card-${index}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/40">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center text-[11px] font-bold text-emerald-700">
            {index}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {asset.name || '—'}
              </span>
              {asset.stage && <Badge className={`text-[10px] border ${stageColor(asset.stage)}`}>{asset.stage}</Badge>}
              {asset.therapeutic_area && <span className="text-[11px] text-slate-500">· {asset.therapeutic_area}</span>}
            </div>
            {asset.indication && (
              <p className="text-xs text-slate-600 mt-1 leading-relaxed" title={asset.indication}>
                {asset.indication}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Per-asset stats row */}
      <div className="px-5 py-3 border-b border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-4">
        <AssetStat label="Pharmas Contacted" value={pharmas} tone="violet" />
        <AssetStat label="Actively Evaluating" value={positive} tone="amber" highlight={positive > 0} />
        <AssetStat label="Awaiting Trial Data" value={awaitingTrial} tone="orange" highlight={awaitingTrial > 0} />
        <AssetStat label="Declined" value={negative} tone="rose" dim={negative === 0} />
      </div>

      {/* Per-asset brief — dynamic, synthesizes targets + responses + decline themes */}
      {asset.brief && (
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/40">
          <div className="flex items-start gap-3">
            <p className="text-xs text-slate-700 leading-relaxed flex-1" data-testid={`asset-${index}-brief`}>{asset.brief}</p>
            <CopyButton text={asset.brief} label="Copy brief" testid={`asset-${index}-copy-brief`} />
          </div>
        </div>
      )}

      {/* Pharmas the biotech is already in conversation with directly —
          surfaced for transparency so the public viewer sees the full
          picture (suppressed from AI mapping, fed back as KAL signal). */}
      {selfManaged.length > 0 && (
        <div className="px-5 py-3 border-b border-slate-100 bg-amber-50/30" data-testid={`asset-${index}-self-managed`}>
          <div className="flex items-center gap-2 mb-1.5">
            <Info className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Self-managed by biotech</span>
            <span className="text-[10px] text-slate-400">({selfManaged.length})</span>
          </div>
          <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">
            Pharmas the biotech is already engaging directly — kept outside this campaign.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {selfManaged.map((name, i) => (
              <Badge
                key={i}
                variant="secondary"
                className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100"
                data-testid={`asset-${index}-self-managed-chip-${i}`}
              >
                {name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Per-asset response rows (if any) */}
      {responses.length > 0 && (
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Pharma Responses</span>
            <span className="text-[10px] text-slate-400">({responses.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {responses.map((r, i) => (
              <ResponseRow key={i} entry={r} testid={`asset-${index}-response-${i}`} compact />
            ))}
          </div>
        </div>
      )}

      {/* Per-asset target companies (collapsible) */}
      {targets.length > 0 && (
        <div className="px-5 py-3">
          <button
            type="button"
            onClick={() => setTargetsOpen(!targetsOpen)}
            className="flex items-center gap-2 text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
            data-testid={`toggle-targets-${index}`}
          >
            {targetsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <Target className="w-3.5 h-3.5 text-violet-600" />
            <span>Full Target List for this Asset</span>
            <span className="text-[10px] text-slate-400">({targets.length})</span>
          </button>
          {targetsOpen && (
            <div className="mt-3 max-h-[420px] overflow-y-auto space-y-1.5">
              {targets.map((tc, i) => {
                const isExcluded = tc.approval_status === 'rejected';
                const exclusionNote = (tc.approval_comment || '').trim() || "Excluded at client's request";
                const isExpanded = expandedTargets.has(i);
                const hasRationale = !!(tc.rationale && tc.rationale.trim());
                const hasMeta = !!(tc.priority_tier || tc.fit_category || tc.buyer_archetype || tc.deal_type);
                const canExpand = hasRationale || hasMeta;
                const toggleExpand = () => {
                  if (!canExpand) return;
                  setExpandedTargets(prev => {
                    const next = new Set(prev);
                    next.has(i) ? next.delete(i) : next.add(i);
                    return next;
                  });
                };
                return (
                  <div key={i}
                    className={`rounded-md border transition-colors ${
                      isExcluded
                        ? 'bg-rose-50/60 border-rose-200'
                        : isExpanded
                          ? 'bg-white border-emerald-200 shadow-sm'
                          : 'bg-slate-50/60 border-slate-100 hover:border-slate-200'
                    }`}
                    data-testid={`asset-${index}-target-${i}`}
                  >
                    {/* Header row — clickable to expand if we have rationale */}
                    <button
                      type="button"
                      onClick={toggleExpand}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left ${canExpand ? 'cursor-pointer' : 'cursor-default'}`}
                      data-testid={`asset-${index}-target-${i}-toggle`}
                    >
                      <span className="text-[9px] font-mono text-slate-400 shrink-0 w-5">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className={`text-[12px] font-semibold truncate ${
                          isExcluded ? 'text-rose-700 line-through decoration-rose-400/60' : 'text-slate-800'
                        }`} title={tc.company_name}>
                          {tc.company_name}
                        </p>
                        {isExcluded ? (
                          <p className="text-[10px] text-rose-600 truncate" title={exclusionNote}>
                            🚫 {exclusionNote}
                          </p>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap text-[10px] text-slate-500">
                            {tc.country && (
                              <span className="inline-flex items-center gap-0.5">
                                <MapPin className="w-2.5 h-2.5" />{tc.country}
                              </span>
                            )}
                            {tc.priority_tier && (
                              <span className="inline-flex items-center gap-0.5 px-1 rounded bg-violet-50 text-violet-700 border border-violet-100">
                                {tc.priority_tier}
                              </span>
                            )}
                            {tc.fit_score > 0 && (
                              <span className="text-emerald-700 font-semibold">Fit {tc.fit_score}/100</span>
                            )}
                          </div>
                        )}
                      </div>
                      {canExpand && !isExcluded && (
                        <span className="shrink-0 text-[10px] text-slate-400 hover:text-slate-600 transition-colors">
                          {isExpanded ? '▾ Hide' : '▸ Why?'}
                        </span>
                      )}
                    </button>
                    {/* Expanded rationale & buyer-fit context */}
                    {isExpanded && !isExcluded && (
                      <div className="px-2.5 pb-2.5 pt-1 border-t border-slate-100 space-y-1.5" data-testid={`asset-${index}-target-${i}-detail`}>
                        {hasRationale && (
                          <div>
                            <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-0.5">Why we approached them</p>
                            <p className="text-[11px] text-slate-700 leading-relaxed">{tc.rationale}</p>
                          </div>
                        )}
                        {hasMeta && (
                          <div className="flex flex-wrap gap-1.5 pt-0.5">
                            {tc.fit_category && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100" title="Buyer-fit category">
                                <span className="text-emerald-500 mr-1">Fit:</span>{tc.fit_category}
                              </span>
                            )}
                            {tc.buyer_archetype && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100" title="Buyer archetype profile">
                                <span className="text-sky-500 mr-1">Buyer:</span>{tc.buyer_archetype}
                              </span>
                            )}
                            {tc.deal_type && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100" title="Likely deal structure">
                                <span className="text-amber-500 mr-1">Deal:</span>{tc.deal_type}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** Compact stat used at top-level AND inside asset cards. */
function Stat({ label, value, icon: Icon, tone = 'slate', dim = false }) {
  const toneClass = {
    fuchsia: 'text-fuchsia-600 bg-fuchsia-50',
    violet: 'text-violet-600 bg-violet-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    indigo: 'text-indigo-600 bg-indigo-50',
    amber: 'text-amber-600 bg-amber-50',
    rose: 'text-rose-600 bg-rose-50',
    slate: 'text-slate-500 bg-slate-100',
  }[tone] || 'text-slate-500 bg-slate-100';
  return (
    <div className={`flex items-center gap-3 ${dim ? 'opacity-70' : ''}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${toneClass}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold text-slate-900 leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 font-medium">{label}</p>
      </div>
    </div>
  );
}

/** Small stat used inside AssetCard — no icon, denser. */
function AssetStat({ label, value, tone = 'slate', highlight = false, dim = false }) {
  const valueColor = {
    violet: 'text-violet-700',
    amber: 'text-amber-700',
    orange: 'text-orange-600',
    rose: 'text-rose-600',
    emerald: 'text-emerald-700',
    slate: 'text-slate-700',
  }[tone] || 'text-slate-700';
  return (
    <div className={`${dim ? 'opacity-50' : ''} ${highlight ? 'animate-pulse-subtle' : ''}`}>
      <p className={`text-lg font-bold leading-none ${valueColor}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 font-medium">{label}</p>
    </div>
  );
}

/** A single response card. Compact mode is used inside AssetCard. */
function ResponseRow({ entry, testid, anonymize, compact }) {
  const def = STATUS_DEFINITIONS[entry.status];
  // Multi-evaluator view: when 2+ contacts at the same pharma are engaged,
  // surface every designation so the biotech sees the full pharma team
  // working on their asset. Falls back to the single representative
  // designation/initials when there's only one contact.
  const contacts = Array.isArray(entry.contacts) ? entry.contacts : [];
  const hasMultiple = contacts.length > 1;
  // Generalize the contact's role so biotechs can't cross-reference the
  // exact title + Pharma + LinkedIn photo to identify the person. Country
  // is also dropped — Singapore-only would narrow a BD VP at Menarini
  // down to one person almost immediately. User request 2026-02.
  const personLabel = coarseDesignation(entry.designation) || entry.contact_initials;
  const isNegative = ['Declined', 'Not Interested', 'Deal Lost', 'Ghosted'].includes(entry.status);
  const notesClass = isNegative
    ? 'text-xs italic text-rose-600 mt-1.5 leading-relaxed whitespace-pre-line font-medium'
    : 'text-xs italic text-slate-600 mt-1.5 leading-relaxed whitespace-pre-line';
  return (
    <Card className={`border border-slate-200 bg-white ${compact ? '' : ''}`} data-testid={testid}>
      <CardContent className={`${compact ? 'p-3' : 'p-4'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              {anonymize ? toInitials(entry.company_name) : entry.company_name}
              {hasMultiple && (
                <span className="text-xs font-medium text-blue-600 ml-1.5">· {contacts.length} evaluators engaged</span>
              )}
            </p>
            {!anonymize && !hasMultiple && personLabel && (
              <p className="text-[11px] text-slate-500 mt-0.5">{personLabel}</p>
            )}
            {!anonymize && hasMultiple && (
              <ul className="mt-1.5 space-y-0.5">
                {contacts.map((c, i) => (
                  <li key={i} className="text-[11px] text-slate-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-slate-300 rounded-full" />
                    <span className="font-medium">{coarseDesignation(c.designation) || c.initials}</span>
                  </li>
                ))}
              </ul>
            )}
            {/* Prefer the AI-generated logical brief; fall back to the
                raw `status_notes` only if the brief hasn't generated yet
                (first uncached load races the LLM call). 2026-06-06 user
                request: the public link should give a logical brief at
                company level, not raw rejection quotes. */}
            {entry.public_brief ? (
              <p className={notesClass} title={entry.public_brief} data-testid={`public-brief-${entry.company_name}`}>
                {entry.public_brief}
              </p>
            ) : entry.status_notes && (
              <p className={notesClass} title={entry.status_notes}>{entry.status_notes}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge
              className={`text-[10px] border whitespace-nowrap inline-flex items-center gap-1 ${STATUS_COLORS[entry.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}
              title={def || entry.status}
            >
              <span>{entry.status}</span>
              {def && <Info className="w-2.5 h-2.5 opacity-60" aria-label={def} />}
            </Badge>
            {/* Mixed signal: this company's best status is still positive but
                one or more individual contacts have declined. Show a small
                rose sub-pill so the public viewer doesn't miss the nuance. */}
            {entry.mixed_decline && (entry.declined_count || 0) > 0 && (
              <Badge
                className="text-[9px] bg-rose-50 text-rose-700 border border-rose-200 whitespace-nowrap"
                title="At least one contact at this pharma has declined; others are still evaluating."
                data-testid={`mixed-decline-${entry.company_name}`}
              >
                {entry.declined_count} contact{entry.declined_count > 1 ? 's' : ''} declined
              </Badge>
            )}
            {entry.updated_at && (
              <span className="text-[10px] text-slate-400 flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" />
                {new Date(entry.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
